import { useEffect, useRef } from 'react';
import { RoomInfo, CAR_COLORS, CarColor, PHYSICS_CONSTANTS } from '@shared';
import { useGameStore } from '../store/gameStore';
import { useNetworkStore } from '../store/networkStore';
import './GameHUD.css';

interface GameHUDProps {
  room: RoomInfo;
  localPlayerId: string | null;
  raceTimer: number;
  showMinimap: boolean;
}

function GameHUD({ room, localPlayerId, raceTimer, showMinimap }: GameHUDProps) {
  const { cars } = useGameStore();
  const currentTrack = useNetworkStore(state => state.track);
  const minimapCanvasRef = useRef<HTMLCanvasElement>(null);
  
  const localPlayer = room.players.find((p) => p.id === localPlayerId);
  const localCar = localPlayerId ? cars.get(localPlayerId) : null;

  // Sort players by position (lap + checkpoint progress)
  const sortedPlayers = [...room.players].sort((a, b) => {
    if (a.finished && !b.finished) return -1;
    if (!a.finished && b.finished) return 1;
    if (a.lap !== b.lap) return b.lap - a.lap;
    return b.checkpointIndex - a.checkpointIndex;
  });

  const localPosition = sortedPlayers.findIndex(p => p.id === localPlayerId) + 1;

  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    const millis = Math.floor((ms % 1000) / 10);
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${millis.toString().padStart(2, '0')}`;
  };

  const getPositionSuffix = (pos: number) => {
    if (pos === 1) return 'st';
    if (pos === 2) return 'nd';
    if (pos === 3) return 'rd';
    return 'th';
  };

  return (
    <div className="game-hud">
      {/* Top left - Race info */}
      <div className="hud-top-left">
        <div className="position-display">
          <span className="position-number">{localPosition}</span>
          <span className="position-suffix">{getPositionSuffix(localPosition)}</span>
          <span className="position-total">/ {room.players.length}</span>
        </div>
        
        <div className="lap-display">
          <span className="lap-label">LAP</span>
          <span className="lap-current">{localPlayer?.lap || 0}</span>
          <span className="lap-separator">/</span>
          <span className="lap-total">{room.lapCount}</span>
        </div>
      </div>

      {/* Top center - Timer */}
      <div className="hud-top-center">
        <div className="timer-display">
          {formatTime(raceTimer)}
        </div>
        {localPlayer?.bestLapTime && (
          <div className="best-lap">
            Best: {formatTime(localPlayer.bestLapTime)}
          </div>
        )}
      </div>

      {/* Top right - Leaderboard */}
      <div className="hud-top-right">
        <div className="mini-leaderboard">
          {sortedPlayers.slice(0, 5).map((player, index) => (
            <div 
              key={player.id} 
              className={`leaderboard-entry ${player.id === localPlayerId ? 'local' : ''}`}
            >
              <span className="entry-position">{index + 1}</span>
              <div 
                className="entry-color"
                style={{ backgroundColor: CAR_COLORS[player.color as CarColor]?.hex || '#888' }}
              />
              <span className="entry-name">
                {player.nickname.length > 10 
                  ? player.nickname.slice(0, 10) + '...' 
                  : player.nickname
                }
              </span>
              {player.finished && <span className="finished-flag">🏁</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom left - Speed/Controls hint */}
      <div className="hud-bottom-left">
        <div className="controls-hint">
          <span>WASD / Arrows to drive</span>
          <span>SPACE for boost</span>
          <span>R to respawn</span>
        </div>
        
        {/* Nitro Gauge */}
        {localCar && (
          <div className="nitro-gauge">
            <span className="nitro-label">NITRO</span>
            <div className="nitro-bar-container">
              <div 
                className="nitro-bar-fill"
                style={{ 
                  width: `${(localCar.nitroAmount / PHYSICS_CONSTANTS.NITRO_MAX) * 100}%`,
                  backgroundColor: localCar.nitroAmount > 20 ? '#00d9ff' : '#ff4444'
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Minimap */}
      {showMinimap && currentTrack && (
        <div className="hud-minimap">
          <div className="minimap-container">
            <MinimapCanvas track={currentTrack} cars={cars} players={room.players} localPlayerId={localPlayerId} />
          </div>
        </div>
      )}
    </div>
  );
}

// Separate minimap component to handle canvas drawing
function MinimapCanvas({ track, cars, players, localPlayerId }: {
  track: { width: number; height: number; roadPath?: any[]; boundaries?: any[] };
  cars: Map<string, any>;
  players: any[];
  localPlayerId: string | null;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !track) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const minimapSize = 150;
    const scaleX = minimapSize / track.width;
    const scaleY = minimapSize / track.height;
    const scale = Math.min(scaleX, scaleY);
    const offsetX = (minimapSize - track.width * scale) / 2;
    const offsetY = (minimapSize - track.height * scale) / 2;
    
    // Clear canvas
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, minimapSize, minimapSize);
    
    // Draw road paths
    if (track.roadPath) {
      track.roadPath.forEach((path: any) => {
        if (path.points && path.points.length > 1) {
          ctx.strokeStyle = '#3a3a5e';
          ctx.lineWidth = Math.max(2, (path.width || 200) * scale);
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(offsetX + path.points[0].x * scale, offsetY + path.points[0].y * scale);
          for (let i = 1; i < path.points.length; i++) {
            ctx.lineTo(offsetX + path.points[i].x * scale, offsetY + path.points[i].y * scale);
          }
          ctx.stroke();
        }
      });
    }
    
    // Draw boundaries
    if (track.boundaries) {
      track.boundaries.forEach((boundary: any) => {
        if (boundary.points && boundary.points.length > 1) {
          ctx.strokeStyle = '#ff4444';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(offsetX + boundary.points[0].x * scale, offsetY + boundary.points[0].y * scale);
          for (let i = 1; i < boundary.points.length; i++) {
            ctx.lineTo(offsetX + boundary.points[i].x * scale, offsetY + boundary.points[i].y * scale);
          }
          if (boundary.isClosed) {
            ctx.closePath();
          }
          ctx.stroke();
        }
      });
    }
    
    // Draw cars
    players.forEach(player => {
      const car = cars.get(player.id);
      if (!car) return;
      
      const x = offsetX + car.displayPosition.x * scale;
      const y = offsetY + car.displayPosition.y * scale;
      const color = CAR_COLORS[player.color as CarColor]?.hex || '#888';
      const isLocal = player.id === localPlayerId;
      
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, isLocal ? 4 : 3, 0, Math.PI * 2);
      ctx.fill();
      
      if (isLocal) {
        ctx.strokeStyle = '#00d9ff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    });
  }, [track, cars, players, localPlayerId]);
  
  return <canvas ref={canvasRef} width={150} height={150} />;
}

export default GameHUD;
